import { IconButton, TextField } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  envVarRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: 'center',
  },
}));

interface EnvVarRowProps {
  envKey: string;
  envValue: string;
  onUpdate: (key: string, value: string) => void;
  onDelete: (key: string) => void;
  onRename: (oldKey: string, newKey: string) => void;
}

export const EnvVarRow = ({ envKey, envValue, onUpdate, onDelete, onRename }: EnvVarRowProps) => {
  const classes = useStyles();
  const [localKey, setLocalKey] = useState(envKey);
  const [localValue, setLocalValue] = useState(envValue);
  const keyUpdateTimeoutRef = useRef<NodeJS.Timeout>();
  const valueUpdateTimeoutRef = useRef<NodeJS.Timeout>();
  const localKeyRef = useRef(envKey);
  const localValueRef = useRef(envValue);
  const lastSavedKeyRef = useRef(envKey);
  const lastSavedValueRef = useRef(envValue);

  useEffect(() => {
    localKeyRef.current = localKey;
  }, [localKey]);

  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  useEffect(() => {
    setLocalKey(envKey);
    setLocalValue(envValue);
    lastSavedKeyRef.current = envKey;
    lastSavedValueRef.current = envValue;
  }, [envKey, envValue]);

  useEffect(() => {
    return () => {
      // Save pending changes on unmount
      if (keyUpdateTimeoutRef.current) {
        clearTimeout(keyUpdateTimeoutRef.current);
      }
      if (valueUpdateTimeoutRef.current) {
        clearTimeout(valueUpdateTimeoutRef.current);
      }

      const trimmedKey = localKeyRef.current.trim();
      // Apply rename first if key changed
      if (trimmedKey !== lastSavedKeyRef.current && trimmedKey !== '') {
        onRename(lastSavedKeyRef.current, trimmedKey);
        lastSavedKeyRef.current = trimmedKey;
      }

      // Save value using the canonical/saved key
      if (localValueRef.current !== lastSavedValueRef.current) {
        onUpdate(lastSavedKeyRef.current, localValueRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.envVarRow}>
      <TextField
        label="Key"
        value={localKey}
        onChange={e => {
          const newKey = e.target.value;
          setLocalKey(newKey);

          if (keyUpdateTimeoutRef.current) {
            clearTimeout(keyUpdateTimeoutRef.current);
          }

          keyUpdateTimeoutRef.current = setTimeout(() => {
            const trimmedKey = newKey.trim();
            if (trimmedKey !== lastSavedKeyRef.current && trimmedKey !== '') {
              onRename(lastSavedKeyRef.current, trimmedKey);
              lastSavedKeyRef.current = trimmedKey;
            }
          }, 300);
        }}
        onBlur={() => {
          // Clear timeout and update immediately on blur
          if (keyUpdateTimeoutRef.current) {
            clearTimeout(keyUpdateTimeoutRef.current);
          }
          const trimmedKey = localKey.trim();
          if (trimmedKey !== lastSavedKeyRef.current && trimmedKey !== '') {
            onRename(lastSavedKeyRef.current, trimmedKey);
            lastSavedKeyRef.current = trimmedKey;
          } else if (trimmedKey === '') {
            setLocalKey(lastSavedKeyRef.current);
          }
        }}
        variant="outlined"
        size="small"
        style={{ flex: 1 }}
        placeholder="e.g., AWS_REGION"
      />
      <TextField
        label="Value"
        value={localValue}
        onChange={e => {
          const newValue = e.target.value;
          setLocalValue(newValue);

          // Clear existing timeout
          if (valueUpdateTimeoutRef.current) {
            clearTimeout(valueUpdateTimeoutRef.current);
          }

          // Update parent after a short delay using canonical/saved key (debounce)
          valueUpdateTimeoutRef.current = setTimeout(() => {
            onUpdate(lastSavedKeyRef.current, newValue);
            lastSavedValueRef.current = newValue;
          }, 300);
        }}
        onBlur={() => {
          // Clear timeout and update immediately on blur
          if (valueUpdateTimeoutRef.current) {
            clearTimeout(valueUpdateTimeoutRef.current);
          }
          if (localValue !== envValue) {
            onUpdate(lastSavedKeyRef.current, localValue);
            lastSavedValueRef.current = localValue;
          }
        }}
        variant="outlined"
        size="small"
        style={{ flex: 2 }}
        placeholder="e.g., us-east-1"
      />
      <IconButton size="small" onClick={() => onDelete(lastSavedKeyRef.current)} color="secondary">
        <DeleteIcon />
      </IconButton>
    </div>
  );
};
